import Link from "next/link";

import { ArrowRight, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FormattedNumber } from "@/components/ui/number-flow";
import { PRICING_PLANS } from "@/lib/constants/pricing";

import type { PricingCard } from "@/pages/partners/[partnerName]";

export function SoloPricingCardPartner({ pricingCard, ctaLink }: { pricingCard: PricingCard; ctaLink: string }) {
  const price = pricingCard.fields.price || PRICING_PLANS.pro.price;
  const discountPercentage = pricingCard.fields.discountPercentage || 0;
  const frequency = pricingCard.fields.frequency || "month";

  return (
    <Card className="relative flex flex-col gap-5 overflow-hidden rounded-3xl">
      <div className="flex flex-col gap-6 px-6 md:px-8 p-8 bg-ash-100 text-black rounded-2xl h-full">
        <div className="flex flex-col gap-6 w-full h-full">
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-2 leading-[1.1] tracking-snug pb-5 md:pb-6 border-b border-ash-300">
            <div className="flex flex-col gap-1 font-bold">
              <p className="text-2xl tracking-tight leading-[1.4] text-black font-clash">{pricingCard.fields.header}</p>
              <p className="text-xl text-rebolt-500 leading-normal">{pricingCard.fields.subheader}</p>
            </div>
            <div className="flex flex-col items-start md:items-end">
              {discountPercentage > 0 && (
                <div className="flex items-center gap-1 text-2xl tracking-tight text-black font-medium relative">
                  <FormattedNumber
                    format={{
                      style: "currency",
                      currency: "USD",
                      trailingZeroDisplay: "stripIfInteger",
                    }}
                    value={price}
                    className="font-clash"
                  />
                  <p className="-mt-1">/{frequency}</p>
                  <span className="absolute top-1/2 left-0 w-full h-[2px] bg-lava-500 -translate-y-1/2" />
                </div>
              )}
              {typeof price === "number" ? (
                <div className="flex items-center gap-1 text-6xl font-bold tracking-tight -mt-2">
                  <FormattedNumber
                    format={{
                      style: "currency",
                      currency: "USD",
                      trailingZeroDisplay: "stripIfInteger",
                    }}
                    value={Math.floor(price * (1 - discountPercentage / 100))}
                    className="font-clash"
                  />
                  <p className="text-2xl mt-1">/{frequency}</p>
                </div>
              ) : (
                <h1 className="text-4xl font-medium">{price}</h1>
              )}
            </div>
          </div>

          <div className="space-y-4 pb-6 border-b border-ash-300 h-full">
            <h3 className="font-medium text-ash-500"> Includes:</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              {pricingCard.fields.includes.map((include) => (
                <div className="flex gap-2" key={include}>
                  <Check className="w-6 h-6 text-rebolt-500" />
                  <p className="font-medium text-sm sm:text-base">{include}</p>
                </div>
              ))}
            </div>
          </div>

          <Link href={ctaLink} target="_blank" rel="noopener noreferrer">
            <Button variant="rebolt-primary" className="w-full py-3 px-6 h-fit">
              {pricingCard.fields.buttonText || "Get a new website"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </Card>
  );
}
export function MultiplePricingCardPartner({ pricingCard, ctaLink }: { pricingCard: PricingCard; ctaLink: string }) {
  const price = pricingCard.fields.price || PRICING_PLANS.pro.price;
  const discountPercentage = pricingCard.fields.discountPercentage || 0;
  const frequency = pricingCard.fields.frequency || "month";
  return (
    <Card className="relative flex flex-col gap-5 overflow-hidden rounded-3xl border-2 border-rebolt-500 ">
      <div className="flex flex-col gap-6 px-6 md:px-8 p-8 bg-ash-100 text-black rounded-2xl h-full ">
        <div className="flex flex-col gap-6 w-full h-full">
          <div className="relative flex flex-col justify-between gap-2 leading-[1.1] tracking-snug pb-6 border-b border-ash-300">
            <div className="flex flex-col gap-1 font-bold">
              <p className="text-2xl tracking-tight leading-[1.4] text-black font-clash">{pricingCard.fields.header}</p>
              <p className="text-xl text-rebolt-500 leading-normal">{pricingCard.fields.subheader}</p>
            </div>
            <div className="flex flex-col">
              {typeof price === "number" ? (
                <div className="flex items-center gap-1 text-6xl font-bold tracking-tight ">
                  <FormattedNumber
                    format={{
                      style: "currency",
                      currency: "USD",
                      trailingZeroDisplay: "stripIfInteger",
                    }}
                    value={Math.floor(price * (1 - discountPercentage / 100))}
                    className="font-clash"
                  />
                  <p className="text-2xl mt-1">/{frequency}</p>
                </div>
              ) : (
                <h1 className="text-4xl font-medium">{price}</h1>
              )}
              {discountPercentage > 0 && (
                <div className="flex items-center gap-1 text-2xl tracking-tight text-black font-medium relative w-fit -mt-2">
                  <FormattedNumber
                    format={{
                      style: "currency",
                      currency: "USD",
                      trailingZeroDisplay: "stripIfInteger",
                    }}
                    value={price}
                    className="font-clash"
                  />
                  <p className="-mt-1">/{frequency}</p>
                  <span className="absolute top-1/2 left-0 w-full h-[2px] bg-lava-500 -translate-y-1/2" />
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4 pb-6 border-b border-ash-300 h-full">
            <h3 className="font-medium text-ash-500"> Includes:</h3>
            <div className="grid grid-cols-1 gap-x-6 gap-y-4">
              {pricingCard.fields.includes.map((include) => (
                <div className="flex gap-2" key={include}>
                  <Check className="w-6 h-6 text-rebolt-500" />
                  <p className="font-medium text-sm sm:text-base">{include}</p>
                </div>
              ))}
            </div>
          </div>

          <Link href={ctaLink} target="_blank" rel="noopener noreferrer">
            <Button variant="rebolt-primary" className="w-full py-3 px-6 h-fit">
              {pricingCard.fields.buttonText || "Get a new website"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </Card>
  );
}
